import ReactDom from 'react-dom';

import { useState } from '@hookstate/core';
import { Image } from '@mantine/core';

import { Icon as BlueIcon } from '@blueprintjs/core';

import AvatarDefaultImg from '@assets/svg/avatar-placeholder.svg';

import { useLocalStorage, useToast } from '@hooks';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { getUserMembers } from '@services/queries/studios';
import { useParams } from 'react-router-dom';
import { changeUpdateStatus } from '@services/queries/updates/changeUpdateStatus/changeUpdateStatus';

import { useDispatch } from 'react-redux';
import { useContext } from 'react';
import { NavigationContext } from '@layout/navigation/navigation';
import { addUpdatedUpdate } from '@store/redux/features/updatesOptimistic';
import { addBackUpUpdatedUpdates } from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';
import { SearchFigma } from '@components/SearchBars';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Label,
  Item,
  Icon,
  Avatar,
  Items,
  ItemNoFeedback,
  Remove,
  UsersSeparator,
} from './ReAssignDialog.styles';
import { ReAssignDialogLoading } from './ReAssignDialog.loading';

interface IRecentUser {
  id: string;
  date: Date;
}

interface IReAssignDialog {
  open: boolean;
  onClose: () => void;
  updateStatusId: string;
  updateId: string;
  assigneeId?: string;
  closeOnFinish?: boolean;
}

export const ReAssignDialog = ({
  open,
  onClose,
  assigneeId,
  updateId,
  updateStatusId,
  closeOnFinish = true,
}: IReAssignDialog) => {
  const portalDiv = document.getElementById('portal');

  const params = useParams<{ id: string; equipmentId: string }>();

  const { toast } = useToast();

  const { updatesMtViewTime } = useContext(NavigationContext);

  const [recentUsers, setRecentUsers] = useLocalStorage<IRecentUser[]>(
    'recentUpdateAssigneeUsers',
    [],
  );

  const queryClient = useQueryClient();

  const searchUser = useState<string>('');

  const { data, isLoading, isError } = useQuery('members', getUserMembers);

  const reloadQueries = async () => {
    queryClient.invalidateQueries(['updateStatuses', updateId]);

    toast.success('Update changed successfully');
    queryClient.invalidateQueries(['updateStatuses']);
    queryClient.invalidateQueries(['updatesMetrics']);
    queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
    queryClient.invalidateQueries([
      'equipments',
      params.equipmentId,
      'updateStatuses',
    ]);
  };

  const dispatch = useDispatch();

  const mutation = useMutation(changeUpdateStatus, {
    onSuccess: async (newUpdate, { assigneeId: assignee }) => {
      if (closeOnFinish) {
        onClose();
      }
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));
      reloadQueries();

      if (assignee) {
        setRecentUsers(
          [
            { id: assignee, date: new Date() },
            ...recentUsers.filter(item => item.id !== assignee),
          ].slice(0, 5),
        );
      }
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const findCurrentUser = data?.find(item => item.id === assigneeId);

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>{assigneeId ? 'Change Assignee' : 'Assign to'}</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            <SearchFigma
              placeholder="User name or Email"
              startSearch={searchUser.set}
              onClear={() => {
                searchUser.set('');
              }}
            />

            <Items>
              {isLoading && <ReAssignDialogLoading />}

              {!isLoading && (
                <div>
                  {findCurrentUser && (
                    <>
                      <Label className="h1">Current Assignee</Label>
                      <ItemNoFeedback>
                        <Icon>
                          <Image
                            src={findCurrentUser.picture || AvatarDefaultImg}
                            alt=""
                            radius={12}
                            width="24px"
                            height="24px"
                            placeholder={
                              <Avatar
                                className="defaultImage"
                                src={AvatarDefaultImg}
                                alt=""
                              />
                            }
                          />
                        </Icon>
                        <Label>{findCurrentUser.name}</Label>
                        <Remove
                          type="button"
                          onClick={() => {
                            if (!mutation.isLoading) {
                              mutation.mutate({
                                updateStatusId,
                                assigneeId: null,
                              });
                            }
                          }}
                        >
                          <BlueIcon icon="cross" color="#D14343" size={12} />
                        </Remove>
                      </ItemNoFeedback>
                    </>
                  )}

                  {!isLoading &&
                    !isError &&
                    data &&
                    (() => {
                      const filteredUsers = data
                        .filter(item => item.id !== assigneeId)
                        .filter(item =>
                          searchUser.get()
                            ? item.name
                                ?.toLowerCase()
                                .includes(searchUser.get().toLowerCase()) ||
                              item.email
                                ?.toLowerCase()
                                .includes(searchUser.get().toLowerCase())
                            : true,
                        );

                      const showRecent =
                        filteredUsers.filter(
                          item =>
                            !!recentUsers.find(
                              userItem => userItem.id === item.id,
                            ),
                        ).length > 0;

                      const showNoRecent =
                        filteredUsers.filter(
                          item =>
                            !recentUsers.find(
                              userItem => userItem.id === item.id,
                            ),
                        ).length > 0;

                      const showSeparator = showRecent && showNoRecent;

                      return (
                        <>
                          {findCurrentUser && (showRecent || showNoRecent) && (
                            <UsersSeparator />
                          )}
                          {showRecent && <Label className="h1">Recent</Label>}
                          {recentUsers
                            .map(item =>
                              filteredUsers.find(
                                userItem => userItem.id === item.id,
                              ),
                            )
                            .filter(
                              item =>
                                !!recentUsers.find(
                                  userItem => userItem.id === item?.id,
                                ),
                            )
                            .map(item => (
                              <Item
                                key={item?.id}
                                onClick={() => {
                                  if (!mutation.isLoading) {
                                    mutation.mutate({
                                      updateStatusId,
                                      assigneeId: item?.id,
                                    });
                                  }
                                }}
                              >
                                <Icon>
                                  <Image
                                    src={item?.picture || AvatarDefaultImg}
                                    alt=""
                                    width="24px"
                                    height="24px"
                                    radius={12}
                                    placeholder={
                                      <Avatar
                                        className="defaultImage"
                                        src={AvatarDefaultImg}
                                        alt=""
                                      />
                                    }
                                  />
                                </Icon>
                                <Label>{item?.name}</Label>
                              </Item>
                            ))}

                          {showRecent && showSeparator && <UsersSeparator />}

                          {filteredUsers
                            .filter(
                              item =>
                                !recentUsers.find(
                                  userItem => userItem.id === item.id,
                                ),
                            )
                            .map(item => (
                              <Item
                                key={item.id}
                                onClick={() => {
                                  if (!mutation.isLoading) {
                                    mutation.mutate({
                                      updateStatusId,
                                      assigneeId: item.id,
                                    });
                                  }
                                }}
                              >
                                <Icon>
                                  <Image
                                    src={item.picture || AvatarDefaultImg}
                                    alt=""
                                    width="24px"
                                    height="24px"
                                    radius={12}
                                    placeholder={
                                      <Avatar
                                        className="defaultImage"
                                        src={AvatarDefaultImg}
                                        alt=""
                                      />
                                    }
                                  />
                                </Icon>
                                <Label>{item.name}</Label>
                              </Item>
                            ))}
                        </>
                      );
                    })()}
                </div>
              )}
            </Items>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
